/**
 * React UFO (Unified Frontend Observability) performance monitoring setup.
 *
 * UFO tracks page load times and reports them as operational events via
 * the analytics web client. Interactions are tracked using @atlaskit/react-ufo:
 *   - traceUFOPageLoad() / traceUFOTransition() are called via useReactUFOWatcher (central, in App.tsx)
 *   - <UFOSegment> provides interaction context to child components
 *   - <UFOLoadHold> holds the interaction open while data is loading; removing
 *     the hold (hold={false}) automatically completes and sends the event.
 *
 * See: https://developer.atlassian.com/platform/ufo/
 */
import { init } from '@atlaskit/react-ufo/interaction-metrics-init';
import { getAnalyticsWebClient } from '@app/analytics/src/analytics';

// ---------------------------------------------------------------------------
// Initialise UFO once at app startup.
// The analytics client is wrapped in the shape react-ufo expects:
//   { getAnalyticsWebClientPromise: () => Promise<{ getInstance: () => client }> }
// ---------------------------------------------------------------------------
export const initUFO = (): void => {
  const analyticsClientPromise = Promise.resolve({
    getAnalyticsWebClientPromise: () =>
      Promise.resolve({
        getInstance: () => getAnalyticsWebClient(),
      }),
  });

  init(analyticsClientPromise, {
    product: 'appUpdates',
    // region is the geographic deployment region (not environment).
    // Defaults to 'us-east-1' — the primary Atlassian region.
    region: process.env.REACT_APP_REGION ?? 'us-east-1',
    vc: {
      enabled: true,
      heatmapSize: 200,
      oldDomUpdates: false,
      devToolsEnabled: true,
      enabledVCRevisions: {
        all: ['fy25.02', 'fy25.03'] as const,
      },
      // Account for possible UGC/PII in DOM attributes:
      // id: false — element IDs may contain user-generated content (e.g. update IDs)
      selectorConfig: {
        id: false,
        role: true,
        className: true,
        testId: true,
      },
    },
    // Prefix aligns experience keys with the platform naming convention
    // used by Performance Portal (e.g. 'ufo-app-updates').
    namePrefix: 'ufo-',
    // Only track page_load, transition and press interactions.
    kind: {
      page_load: 1,
      transition: 1,
      hover: 0,
      legacy: 0,
      typing: 0,
      press: 1,
    },
  });
};
